import {
  buildFeatureFlagsString,
  FEATURE_FLAGS_HTTP_HEADER_NAME,
  FeatureFlags,
} from '@noths/polaris-client-feature-flags';
import { DeliveryZoneCode } from '@noths/polaris-client-localisation';
import { FetchError, fetchJSONWithTimeout, isBrowser } from '@noths/polaris-client-utils';
import { CurrencyCode } from '@noths/polaris-dev-ts-types';
import getConfig from 'next/config';
import qs, { ParsedUrlQueryInput } from 'querystring';

import { NextConfig } from '../../types';
import { BrowseServiceProductsData } from './types';
const { publicRuntimeConfig } = getConfig() as NextConfig;

export const TIMEOUT = {
  server: 5500,
  browser: 15000,
};

const getTimeout = () => (isBrowser() ? TIMEOUT.browser : TIMEOUT.server);

const getBrowseApiEndpoint = () =>
  isBrowser() ? publicRuntimeConfig.endpoints.browse : publicRuntimeConfig.endpoints.browseMesh;

type BrowseProvider = 'google' | 'loop54';

export type BrowseApiPath =
  | 'listings/v1/category'
  | 'listings/v1/partner'
  | 'listings/v1/products'
  | 'listings/v1/search';

export interface FetchBrowseDataArgs {
  cookie?: string;
  currencyCode?: CurrencyCode;
  deliveryZone?: DeliveryZoneCode;
  elevaateUid?: string;
  elevaateSessionId?: string;
  featureFlags?: FeatureFlags;
  query?: ParsedUrlQueryInput;
  previewing?: boolean;
  shortcode?: string;
  userId?: string;
  browseProvider?: BrowseProvider;
}

export const fetchBrowseData = async (
  path: BrowseApiPath,
  {
    cookie,
    currencyCode,
    deliveryZone,
    elevaateUid,
    elevaateSessionId,
    featureFlags,
    previewing,
    query,
    shortcode,
    userId,
    browseProvider,
  }: FetchBrowseDataArgs = {},
) => {
  const querystring = qs.encode({
    ...query,
    ...(currencyCode ? { currencyCode } : {}),
    ...(deliveryZone ? { deliveryZone } : {}),
    ...(previewing ? { previewing } : {}),
    ...(shortcode ? { shortcode } : {}),
    ...(userId ? { userId } : {}),
  });

  const apiResponse = (await fetchJSONWithTimeout(
    `${getBrowseApiEndpoint()}${path}?${querystring}`,
    {
      redirect: 'manual',
      timeout: getTimeout(),
      headers: {
        ...(cookie && { Cookie: cookie }),
        ...(elevaateUid && { 'elevaate-uid': elevaateUid }),
        ...(elevaateSessionId && { 'elevaate-usid': elevaateSessionId }),
        ...(featureFlags && {
          [FEATURE_FLAGS_HTTP_HEADER_NAME]: buildFeatureFlagsString(featureFlags),
        }),
        ...(browseProvider && {
          ...{
            provider: browseProvider,
          },
        }),
      },
    },
  )) as Response;

  if (apiResponse?.status === 301 && apiResponse?.headers.get('location')) {
    const redirectUrl = new URL(apiResponse.headers.get('location') || '');

    return { redirectUrl: `${redirectUrl.pathname}${redirectUrl.search}` };
  }

  if (!apiResponse.ok) {
    const { status, statusText } = apiResponse;

    const { errorCode, errorMessage } = await apiResponse.json();

    throw new FetchError(statusText, status, errorCode, errorMessage);
  }

  const contentType = apiResponse.headers?.get('content-type');
  if (contentType?.includes('application/json')) {
    const data: BrowseServiceProductsData = await apiResponse.json();

    return { data };
  }

  return null;
};
